import { useQuery } from "@tanstack/react-query";

import { guacHttpClient } from "@/lib/http";
import {
  HttpResponse,
  JobCategory,
  JobLiquidity,
  JobLiquidityBreakdown,
  JobPriority,
} from "@/types";

/**
 * One flat per-job row for the Portfolio health matrix. The backend returns the
 * whole portfolio in a single call (joined server-side) — the client never
 * fetches per job.
 */

export const HEALTH_CATEGORIES = [
  "volume",
  "velocity",
  "quality",
  "engagement",
] as const;

export type HealthCategory = (typeof HEALTH_CATEGORIES)[number];

/** 0–100 score per category. */
export type PortfolioHealthScores = Record<HealthCategory, number>;

/**
 * 0–100 sub-signal scores behind the two multi-input components.
 * null = that sub-signal dropped out (no data / not relevant).
 * volume + engagement are single-input and have no sub-scores.
 */
export interface VelocitySubScores {
  deadline: number | null;
  batchDue: number | null;
  stuck: number | null;
}

export interface QualitySubScores {
  lowQuality: number | null;
  conversionTrend: number | null;
}

export interface PortfolioHealthSubScores {
  velocity: VelocitySubScores;
  quality: QualitySubScores;
}

/** Free-text reasoning per category, stored as a single JSON field. */
export type PortfolioHealthReasonings = Partial<Record<HealthCategory, string>>;

export interface PortfolioJobHealth {
  jobId: string;
  scores: PortfolioHealthScores;
  reasonings: PortfolioHealthReasonings;
  computedAt: string;
}

export interface PortfolioMatrixJob {
  id: string;
  title: string;
  category: JobCategory | null;
  // null = no internal-config row exists yet; the table links out to create one
  // rather than editing the category in place.
  internalConfigId: string | null;
  companyId: string;
  companyName: string;
  companyLogoUrl: string | null;
  companySmallLogoUrl?: string | null;
  liquidity?: JobLiquidity | null;
  liquidityBreakdown?: JobLiquidityBreakdown | null;
  health: PortfolioHealthScores | null;
  healthSubScores: PortfolioHealthSubScores | null;
  healthReasons: Record<HealthCategory, string> | null;
  score: number;
  reqsFilled: number;
  reqsTotal: number;
  pipelineCounts: {
    submissions: number;
    interviews: number;
    offers: number;
    placements: number;
  };
  stafferIds: number[];
  customerDeadlineAt: string | null;
  batchDeadlineAt: string | null;
  lastSubmitAt: string | null;
  lastStafferActionAt: string | null;
  revenueOnTable: number;
  pulse: string | null;
  difficulty: "easy" | "moderate" | "difficult" | "very-difficult" | null;
  bottleneck: "customer" | "screening" | "sourcing" | null;
  pulseDescription: string | null;
  sales: number[];
  priority: JobPriority | null;
}

export const portfolioMatrixQueryKey = ["portfolio", "matrix"] as const;

export function useGetPortfolioMatrix() {
  return useQuery({
    queryKey: portfolioMatrixQueryKey,
    queryFn: async () => {
      const res = await guacHttpClient.get<
        PortfolioMatrixJob[],
        HttpResponse<PortfolioMatrixJob[]>
      >("portfolio/matrix");

      return res.data;
    },
  });
}
